import { Component, OnInit, signal, computed } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { Actions, ofType } from '@ngrx/effects';
import { State } from '../../../../core/store';
import { Store } from '@ngrx/store';
import { BenefitStatusCheckModel } from '../../../../models/benefitsModels';
import { CustomFilterModel } from '../../../../models/customFilterModel';
import { DateRangeModel } from '../../../../models/dateRangeModel';
import { PagingResultsModel } from '../../../../models/pagingResultsModel';
import { DropdownFilterService } from '../../../../core/services/dropdown-filter.service';
import { deepClone, downloadFile } from '../../../../helpers/utils';
import { loadEmployeeUsers, loadViewableCampuses } from '../../../../core/store/employee/employee.action';
import { exportEmployeeBenefitStatusListToExcel, exportEmployeeBenefitStatusListToExcelSuccess, getBenefitStatusList } from '../../../../core/store/benefits/benefits.actions';
import { tap } from 'rxjs';
import { UserAutoCompleteItem, ModifiedSelectItem } from '../../../../models/utilModels';


@Component({
    selector: 'app-list-benefit-eligibility-status',
    templateUrl: './list-benefit-eligibility-status.component.html',
    styleUrls: ['./list-benefit-eligibility-status.component.scss'],
    standalone: false
})
export class ListBenefitEligibilityStatusComponent extends BaseComponent implements OnInit {
  checks: PagingResultsModel<BenefitStatusCheckModel>;
  filter: any;
  canEdit: boolean = false;

  loadingStatusList: boolean = true;
  loadingStatusExcel: boolean = false;

  campusList: ModifiedSelectItem[];
  campusId: number = 0;

  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  userId: number = 0;

  dateRangeList: DateRangeModel[];
  dateRange: DateRangeModel;
  startDate: string = null;
  endDate: string = null;
  isCustomDateRange: boolean = false;

  hourRangeList: DateRangeModel[];
  hourRange: CustomFilterModel;
  minHours: string = null;
  maxHours: string = null;
  isCustomHourRange: boolean = false;

  fullTimeStatusOptions: ModifiedSelectItem[];
  fullTimeStatus: string = null;

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 } 
    ];
  });
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private dropdownFilterService: DropdownFilterService) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.store.select(s => s.benefitsState.loadingBenefitsList).subscribe(loadingStatusList => {
        this.loadingStatusList = loadingStatusList;
      }),
      this.store.select(s => s.benefitsState.loadingBenefitStatusExcel).subscribe(loadingStatusExcel => {
        this.loadingStatusExcel = loadingStatusExcel;
      }),
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEdit = permissionIDs && !!permissionIDs.find(p => p === 3075);
      }),
      this.store.select(s => s.employeeState.campuses).subscribe(campuses => {
        this.campusList = campuses && campuses.length > 0 ? campuses
          : [{ label: 'loading...', value: undefined }];
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.benefitsState.benefitsList).subscribe(statusList => {
        this.checks = statusList;
          if(statusList != null)
            this.totalRecords.set(statusList.totalRecords);
      }),
      this.actions$.pipe(
        ofType(exportEmployeeBenefitStatusListToExcelSuccess),
        tap(action => {
          downloadFile(action.doc, 'EmployeeBenefitStatus.xlsx');
        })
      ).subscribe()
    );

    this.dateRangeList = this.dropdownFilterService.getLast120DayDateRanges();
    if (this.dateRangeList && this.dateRangeList.length > 0) {
      this.dateRange = this.dateRangeList[0];
    }
    this.hourRangeList = this.dropdownFilterService.getFullTimeHourRanges();
    if (this.hourRangeList && this.hourRangeList.length > 0) {
      this.hourRange = this.hourRangeList[0];
    }
    this.fullTimeStatusOptions = [
      { label: 'All', value: null },
      { label: 'Full', value: 'FULL' },
      { label: 'Part', value: 'PART' },
      { label: 'Exempt', value: 'EXEMPT' }
    ];
    this.store.dispatch(loadViewableCampuses());
  }

  changedDateRange() {
    if (this.dateRange.isCustom) {
      this.isCustomDateRange = true;
      return; //reload after manually changing date
    }

    this.isCustomDateRange = false;
    this.startDate = this.dateRange.startDate;
    this.endDate = this.dateRange.endDate;
    this.reloadQueue();
  }

  changedHourRange() {
    if (this.hourRange.isCustom) {
      this.isCustomHourRange = true;
      return; //reload after manually changing date
    }

    this.isCustomHourRange = false;
    this.minHours = this.hourRange.minValue;
    this.maxHours = this.hourRange.maxValue;
    this.reloadQueue();
  }

  reloadFilter(event) {
    this.filter = event;
    this.reloadQueue();
  }

  reloadQueue() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };
    this.filter.filters.minHours = { Name: 'minHours', Value: { value: this.minHours } };
    this.filter.filters.maxHours = { Name: 'maxHours', Value: { value: this.maxHours } };
    this.filter.filters.fullTimeStatus = { Name: 'fullTimeStatus', Value: { value: this.fullTimeStatus } };

    this.store.dispatch(getBenefitStatusList({
      filter: this.filter
    }));
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showInactive: false,
      showTerminated: false
    }));
  }

  reloadListAfterStaffChage(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.reloadQueue();
  }

  reloadListAfterClear(event) {
    
    this.userId = 0;
    this.reloadQueue();
  }

  exportToExcel() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };
    this.filter.filters.minHours = { Name: 'minHours', Value: { value: this.minHours } };
    this.filter.filters.maxHours = { Name: 'maxHours', Value: { value: this.maxHours } };
    this.filter.filters.fullTimeStatus = { Name: 'fullTimeStatus', Value: { value: this.fullTimeStatus } };

    this.store.dispatch(exportEmployeeBenefitStatusListToExcel({
      filter: this.filter
    }));
  }

}
